// #379 — the G100 Test Console's Settings card: the page's one description of the 33 settings of
// protocol 6.14.4 (the server's settings_sync.DEVICE_SETTING_FIELDS, same order), what kind of
// control edits each, and the values the server accepts (owner_api.RULES). Labels live in the
// g100Console locale under settings.fields / settings.choices.
export type G100SettingKind = 'choice' | 'switch' | 'range' | 'windows' | 'timezone' | 'species' | 'zone'

// The 33 names of settings_sync.DEVICE_SETTING_FIELDS, in the server's order; a literal union so
// that the locale keys built from them (settings.fields.<name>, settings.choices.<name>) type-check.
export const G100_SETTING_NAMES = [
  'workMode', 'timerReportValue', 'video', 'photoSize', 'image', 'multishot', 'multishotFlag', 'videoSize',
  'videoLength', 'timelapseFlag', 'timelapseValue', 'pirsentivity', 'delayFlag', 'delayValue', 'timerFlag',
  'timerValue', 'pirswitch', 'stamp', 'sdcycleFlag', 'debugSwitch', 'qlogSwitch', 'keepAliveTime', 'liveRes',
  'ledPower', 'warningLight', 'warningLightMode', 'lightWarningSchedule', 'warningSound', 'warningVolume',
  'soundWarningSchedule', 'timeZone', 'aiSwitch', 'aiDetectionZone',
] as const
export type G100SettingName = (typeof G100_SETTING_NAMES)[number]

// The twelve settings whose values are named one by one (a locale object settings.choices.<name>).
export const G100_CHOICE_NAMES = [
  'workMode', 'video', 'photoSize', 'image', 'multishot', 'videoSize', 'videoLength', 'timerFlag', 'liveRes',
  'ledPower', 'warningLightMode', 'warningVolume',
] as const
export type G100ChoiceName = (typeof G100_CHOICE_NAMES)[number]

interface SpecBase {
  advanced?: boolean     // superuser-only through the owner API; editable on the console (#379)
}
export interface G100ChoiceSpec extends SpecBase {
  name: G100ChoiceName
  kind: 'choice'
  choices: number[]      // the values the server accepts, in the document's order
}
export interface G100OtherSpec extends SpecBase {
  name: G100SettingName
  kind: Exclude<G100SettingKind, 'choice'>
  min?: number           // kind 'range'
  max?: number
}
export type G100SettingSpec = G100ChoiceSpec | G100OtherSpec

const choice = (name: G100ChoiceName, choices: number[]): G100SettingSpec => ({ name, kind: 'choice', choices })
const toggle = (name: G100SettingName, advanced = false): G100SettingSpec => ({ name, kind: 'switch', advanced })
const range = (name: G100SettingName, min: number, max?: number, advanced = false): G100SettingSpec =>
  ({ name, kind: 'range', min, max, advanced })

// Server order. image 2 and videoSize 4 are the only values the owner API takes: the factory's
// own V1.1 comments ("limited to 12MP only", "limited to 2.7K only", CP 225), still the only red
// marks in V1.4. ledPower 1 or 2: in V1.1 the IR LED cannot be switched off.
export const G100_SETTINGS: G100SettingSpec[] = [
  choice('workMode', [1, 2, 3]),
  range('timerReportValue', 1, 24),
  choice('video', [1, 2, 3]),
  choice('photoSize', [0]),
  choice('image', [2]),
  choice('multishot', [1, 2, 3]),
  toggle('multishotFlag'),
  choice('videoSize', [4]),
  choice('videoLength', [5, 10, 15, 20]),
  toggle('timelapseFlag'),
  range('timelapseValue', 1, 1440),
  range('pirsentivity', 1, 9),
  toggle('delayFlag'),
  range('delayValue', 0),
  choice('timerFlag', [0, 1, 2]),
  { name: 'timerValue', kind: 'windows' },
  toggle('pirswitch'),
  toggle('stamp'),
  toggle('sdcycleFlag'),
  toggle('debugSwitch', true),
  toggle('qlogSwitch', true),
  range('keepAliveTime', 0, undefined, true),
  choice('liveRes', [1, 2]),
  choice('ledPower', [1, 2]),
  toggle('warningLight'),
  choice('warningLightMode', [0, 1]),
  { name: 'lightWarningSchedule', kind: 'windows' },
  toggle('warningSound'),
  choice('warningVolume', [60, 75, 90]),
  { name: 'soundWarningSchedule', kind: 'windows' },
  { name: 'timeZone', kind: 'timezone' },
  { name: 'aiSwitch', kind: 'species' },
  { name: 'aiDetectionZone', kind: 'zone' },
]

// The catalogue and the name list describe the same 33 settings in the same order (pinned by a test).
export const G100_SETTINGS_IN_ORDER: boolean =
  G100_SETTINGS.length === G100_SETTING_NAMES.length && G100_SETTINGS.every((spec, i) => spec.name === G100_SETTING_NAMES[i])

/** 6.14.4 window: HHMM-HHMM-days, days 1-7 (the server's _TIMER pattern). */
export const G100_WINDOW = /^(?:[01]\d|2[0-3])[0-5]\d-(?:[01]\d|2[0-3])[0-5]\d-[1-7]{1,7}$/
export const G100_MAX_WINDOWS = 7
/** 6.14.4 aiDetectionZone: 25 cells, the first cell is the lowest bit. */
export const G100_ZONE_CELLS = 25
export const G100_ZONE_ALL = 2 ** G100_ZONE_CELLS - 1

// #403 — the customer sheet (CameraG100SettingsSheet): the same 33 settings in plain words, by group.
// The three advanced settings (superuser-only through the owner API) are never shown there; the
// three the camera cannot change (one accepted value each) are shown as text.
export type G100SettingGroup = 'capture' | 'triggering' | 'deterrence' | 'ai' | 'power'
export const G100_CUSTOMER_GROUPS: G100SettingGroup[] = ['capture', 'triggering', 'deterrence', 'ai', 'power']
export const G100_HIDDEN_FOR_CUSTOMERS = ['keepAliveTime', 'debugSwitch', 'qlogSwitch'] as const
export const G100_FIXED_FOR_CUSTOMERS = ['photoSize', 'image', 'videoSize'] as const
export const G100_CUSTOMER_LAYOUT: Record<G100SettingGroup, G100SettingName[]> = {
  capture: ['video', 'multishotFlag', 'multishot', 'videoLength', 'image', 'photoSize', 'videoSize'],
  triggering: ['pirswitch', 'pirsentivity', 'delayFlag', 'delayValue', 'timelapseFlag', 'timelapseValue', 'timerFlag', 'timerValue'],
  deterrence: ['warningLight', 'warningLightMode', 'lightWarningSchedule', 'warningSound', 'warningVolume', 'soundWarningSchedule'],
  ai: ['aiSwitch', 'aiDetectionZone'],
  power: ['workMode', 'timerReportValue', 'liveRes', 'ledPower', 'stamp', 'sdcycleFlag', 'timeZone'],
}
export const G100_SPEC_BY_NAME: Record<G100SettingName, G100SettingSpec> = Object.fromEntries(
  G100_SETTINGS.map((spec) => [spec.name, spec]),
) as Record<G100SettingName, G100SettingSpec>
